'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { SettingsCard } from '@/shared/ui/settings/SettingsCard';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { EstimateItemRow } from './EstimateItemRow';
import type {
  EstimateItem,
  EstimateSection as Section,
  UnitOption,
  UpdateEstimateItemRequest,
} from '../api/estimate-service';

interface EstimateSectionProps {
  section: Section;
  items: EstimateItem[];
  /** Section notes (estimate-level field) — rendered before the line items. */
  notes: string | null;
  /** Server-driven unit picker options (current measurement system). */
  unitOptions: UnitOption[];
  disabled?: boolean;
  onFieldChange: (itemId: string, data: UpdateEstimateItemRequest) => void;
  onFieldCommit: (itemId: string) => void;
  /** Schedule a debounced header PATCH for the section notes. */
  onNotesChange: (value: string | null) => void;
  /** Flush the pending notes edit immediately (blur). */
  onNotesCommit: () => void;
  onDeleteItem: (itemId: string) => void;
  onAddItem: (section: Section) => void;
  /** Persist a drag-reorder: the section's complete id list in the new order. */
  onReorderItems: (section: Section, itemIds: string[]) => void;
  /** Id of the just-added row (add button / delete-undo restore) — scrolled
   *  into view and flashed once, then consumed via onAddedItemShown. */
  lastAddedItemId?: string | null;
  onAddedItemShown?: () => void;
}

/**
 * Materials / Labor group: the section header carries the "+" add action
 * (Apple parity; the section totals live in the bottom `EstimateTotals`
 * summary), then notes and the line items inside the card. Items reorder via
 * dnd-kit (drag handle on each row); the per-section `DndContext` inherently
 * prevents cross-section drags. Cross-platform order: section title → notes →
 * items. Section title comes from the client (static label) — the notes and
 * items themselves are AI- or user-authored.
 */
export function EstimateSection({
  section,
  items,
  notes,
  unitOptions,
  disabled = false,
  onFieldChange,
  onFieldCommit,
  onNotesChange,
  onNotesCommit,
  onDeleteItem,
  onAddItem,
  onReorderItems,
  lastAddedItemId,
  onAddedItemShown,
}: EstimateSectionProps) {
  const t = useTranslations('common.estimate.editor');

  const [notesDraft, setNotesDraft] = useState(notes ?? '');
  useEffect(() => setNotesDraft(notes ?? ''), [notes]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(items, oldIndex, newIndex);
    onReorderItems(
      section,
      newOrder.map((i) => i.id)
    );
  };

  return (
    <SettingsCard
      title={t(`sections.${section}`)}
      surfaceClassName="bg-estimate-paper"
      headerAction={
        <button
          type="button"
          onClick={() => onAddItem(section)}
          disabled={disabled}
          aria-label={t('addItem')}
          className="inline-flex size-7 items-center justify-center rounded-md text-primary transition-colors hover:bg-muted/50 disabled:opacity-50"
        >
          <Plus className="size-5" strokeWidth={2} />
        </button>
      }
    >
      <div className="divide-y divide-border/40">
        {/* Section notes go BEFORE the line items. No "Notes" label — the
            position (right under the section title) carries the semantics;
            the placeholder covers the empty state. */}
        <div className="px-4 py-3">
          <Textarea
            value={notesDraft}
            onChange={(e) => {
              setNotesDraft(e.target.value);
              onNotesChange(e.target.value || null);
            }}
            onBlur={onNotesCommit}
            disabled={disabled}
            placeholder={t('notesPlaceholder')}
            className="min-h-[72px] resize-none"
            maxLength={2000}
          />
        </div>

        {items.length === 0 ? (
          <p className="px-4 py-3 text-card-meta text-muted-foreground">
            {t('emptySection')}
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              {/* `@container` so EstimateItemRow can switch to its one-line
                  layout by the actual editor-column width (the column narrows
                  in two-column mode, so viewport breakpoints would lie). */}
              <div className="@container divide-y divide-border/40">
                {items.map((item) => (
                  <EstimateItemRow
                    key={item.id}
                    item={item}
                    unitOptions={unitOptions}
                    disabled={disabled}
                    onFieldChange={onFieldChange}
                    onFieldCommit={onFieldCommit}
                    onDelete={onDeleteItem}
                    justAdded={item.id === lastAddedItemId}
                    onJustAddedShown={onAddedItemShown}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </SettingsCard>
  );
}
